<template>
  <div class="plans-page">
    <h2>Choose your pricing plan</h2>
    <div class="plans-container">
      <div
        v-for="plan in plans"
        :key="plan.label"
        :class="['plan', { 'best-value': plan.label === 'Yearly' }]"
      >
        <div v-if="plan.label === 'Yearly'" class="best-value-tag">Best Value</div>
        <div class="plan-name">
          {{ plan.label }}
        </div>
        <div class="price">
          <span class="price-unit">$</span>
          <span :class="['price-amount', { slash: !!plan.sale }]"
            >{{ plan.price
            }}<span v-if="plan.sale" class="sales-price">
              {{ plan.sale }}
            </span></span
          >
          <span class="per-month-text">/ mo.</span>
        </div>
        <div class="billing-period">
          Billed
          <span :class="['total-price', { slash: plan.saleTotal }]"
            >${{ plan.total }}
            <span v-if="plan.saleTotal" class="sale-total">{{ plan.saleTotal }}</span>
          </span>
          {{ plan.charged }}
        </div>
        <a
          class="box-button"
          :href="`https://app.mathtest.ninja/sign-up?plan=${toNumber(plan.label)}`"
          >Select</a
        >
      </div>

      <div class="benefits">
        <div class="benefits-title">Every plan includes</div>
        <ul>
          <li>Money back guarantee</li>
          <li>Unlimited questions</li>
          <li>Variety of quality explanations</li>
          <li>Referral program</li>
        </ul>
        <div class="detail-link-wrapper">
          <router-link to="details" class="detail-link">Learn more </router-link>
        </div>
      </div>
    </div>
    <div class="splash">
      <img src="../assets/students-reading.png" alt="Students reading" />
    </div>
    <!-- Referral program -->
    <div class="referral-container">
      <h2>Want even <em>better</em> deal?</h2>
      <div class="referral-program-subtext">Refer a friend!</div>
      <div class="referral-program-description">
        <p>
          If you refer a friend and that friend uses our service for a month, you get a month free!
          After your fifth referral, every friend you refer gets you two months free!
        </p>
        <p>Use the tool below to see how many free months you can get for the friends you refer.</p>
        <div class="referral-reward-calc">
          <ReferralCalculator />
          <SignupComp />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReferralCalculator from './referral-calculator'
import SignupComp from './signup-comp'
import prices from '../prices'

export default {
  name: 'PlansPage',
  components: {
    ReferralCalculator,
    SignupComp,
  },
  methods: {
    selectPlan(months) {
      location.href = `https://app.mathtest.ninja/sign-up?plan=${months}`
    },
    toNumber(str) {
      switch (str) {
        case 'Yearly':
          return 12
        case 'Quarterly':
          return 3
        case 'Monthly':
          return 1
      }
    },
  },
  computed: {
    plans() {
      return [
        {
          label: 'Yearly',
          price: prices.regular.perMonth.yearly,
          total: prices.regular.total.yearly,
          sale: prices.sale?.perMonth?.yearly ?? 0,
          saleTotal: prices.sale?.total?.yearly ?? 0,
          charged: 'every year',
        },
        {
          label: 'Quarterly',
          price: prices.regular.perMonth.quarterly,
          total: prices.regular.total.quarterly,
          sale: prices.sale?.perMonth?.quarterly ?? 0,
          saleTotal: prices.sale?.total?.quarterly ?? 0,
          charged: 'every 3 months',
        },
        {
          label: 'Monthly',
          price: prices.regular.perMonth.monthly,
          total: prices.regular.total.monthly,
          sale: prices.sale?.perMonth?.monthly ?? 0,
          saleTotal: prices.sale?.total?.monthly ?? 0,
          charged: 'every month',
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
$gutter-width: 20px;

h2 {
  margin: 0;
  padding: 20px;
  font: normal normal normal 32px/42px 'Oswald', sans-serif;
  text-align: center;
}

.plans-container {
  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  grid-gap: $gutter-width;
  padding: $gutter-width;
  max-width: $screen-phone-lg;
  margin: auto;

  @media (min-width: $screen-tablet) {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr 1fr;
    max-width: $screen-tablet;
    grid-auto-flow: reverse;
  }
}

.plan {
  text-align: center;
  padding: 15px;
  border: 1px solid $color-grayscale-300;
  position: relative;

  &.best-value {
    background: $color-grayscale-200;
  }

  .best-value-tag {
    font: normal normal normal 14px/17px proxima-n-w01-reg, sans-serif;
    color: $color-grayscale-100;
    background-color: $color-grayscale-900;
    font-size: 14px;
    position: absolute;
    padding: 5px;
    display: inline-block;
    top: -12px;
    left: calc(50% - 38.3px);
  }

  .plan-name {
    font: normal normal normal 20px/25px proxima-n-w01-reg, sans-serif;
    line-height: 1.5;
    padding: 12px 0;
  }

  .price {
    margin-bottom: 20px;
    position: relative;
  }

  .price-unit {
    vertical-align: top;
    position: relative;
    top: 10px;
  }

  .price-amount {
    font: normal normal normal 70px/87px proxima-n-w01-reg, sans-serif;
    position: relative;
    &.slash::after {
      display: block;
      position: absolute;
      width: 90px;
      content: '';
      height: 4px;
      transform: rotate(35deg);
      top: 40px;
      left: -5px;
      background: red;
    }
  }

  .sales-price {
    font-size: 60px;
    color: red;
    position: absolute;
    top: -40px;
    left: 60px;
    padding: 0 10px;
    transform: rotate(-15deg);
  }

  .per-month-text {
    vertical-align: bottom;
  }

  .billing-period {
    font: normal normal normal 14px/17px proxima-n-w01-reg, sans-serif;
    color: $color-grayscale-600;
    font-size: 12px;
    line-height: 16px;
    position: relative;

    .total-price {
      position: relative;
      &.slash::after {
        display: block;
        position: absolute;
        width: 25px;
        content: '';
        height: 2px;
        transform: rotate(35deg);
        top: 6px;
        left: 3px;
        background: red;
      }
    }
    .sale-total {
      font-size: 20px;
      color: red;
      position: absolute;
      top: -20px;
      left: 10px;
      padding: 0 10px;
      transform: rotate(-15deg);
    }
  }
}

.box-button {
  text-decoration: none;
  display: inline-block;
  width: 100px;
  background: $color-primary-2;
  color: white;
  padding: 10px;
  margin: 20px 0 0;
  outline: none;
  font-size: 14px;

  &:active,
  &:hover {
    background: $color-primary-3;
  }
}

.benefits {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 10px;
  border: 1px solid gray;

  @media (min-width: $screen-tablet) {
    grid-column: span 3;
    position: relative;
  }

  .benefits-title {
    font-weight: bold;
    text-align: center;
    font-size: 20px;
    margin-bottom: 10px;
    font-family: 'Oswald', sans-serif;
  }

  ul {
    list-style: none;
    line-height: 1.75;
    margin: auto;
    padding: 0;
    width: max-content;

    li::before {
      content: '';
      margin-right: 10px;
      width: 5px;
      height: 5px;
      border: 2px solid $color-primary-2;
      display: inline-block;
      border-top: 0;
      border-left: 0;
      transform: rotate(-45deg);
    }
    li {
      padding-left: 5px;
    }
  }
}

.splash {
  overflow: hidden;
  text-align: center;
  img {
    width: 100%;
  }
}

.referral-container {
  max-width: $screen-tablet;
  padding: 20px;
  margin: auto;
  h2 {
    text-transform: uppercase;
    font-size: 28px;
    padding: 0 0 20px;
  }
}

.referral-program-subtext {
  text-align: center;
  font-size: 22px;
}

// .referral-counter-button {
//   background: none;
//   border: 1px solid $color-grayscale-700;
//   outline: none;
//   box-sizing: border-box;

//   &:active {
//     background: $color-primary-2;
//   }

//   &:disabled {
//     visibility: hidden;
//   }
// }
// img {
//   vertical-align: text-bottom;
// }

.detail-link {
  color: $color-primary-2;
  text-decoration: none;
  position: relative;
  font-weight: bold;
  display: inline-block;
  padding: 10px;
  border: 1px solid $color-primary-2;
  margin: 20px 0 0;
  outline: none;
  font-size: 14px;
  &:active,
  &:hover {
    background: $color-primary-2;
    color: white;
  }
}
.detail-link-wrapper {
  text-align: center;
}
</style>
